/* Version G: conservative quantity estimates; progressive enhancement with a DOM fallback. */
(function (root) {
  'use strict';
  const positive = value => Number.isFinite(Number(value)) && Number(value) > 0;
  const ceil = value => Math.ceil(value - Math.max(1, Math.abs(value)) * 1e-10);
  const clean = value => String(value || '').replace(/\u00a0/g, ' ').replace(/[\u00d7\u2715]/g, 'x').toLowerCase();
  const scaleToMetres = (value, unit) => Number(value) / (unit === 'mm' ? 1000 : unit === 'cm' ? 100 : 1);

  function areaFromText(text) {
    const s = clean(text);
    const patterns = [
      /(?:coverage(?: per (?:box|pack))?|pack coverage|each pack contains|covers?|coverage:)\D{0,35}(\d+(?:\.\d+)?)\s*(?:m\s*[²^2]|m2|sqm|sq\.?\s*m|square\s+met(?:re|er)s?|squared?\s+met(?:re|er)s?)/,
      /(\d+(?:\.\d+)?)\s*(?:m\s*[²^2]|m2|sqm|sq\.?\s*m|square\s+met(?:re|er)s?|squared?\s+met(?:re|er)s?)\s*(?:per|\/)?\s*(?:box|pack)/,
      /each pack contains\s+(\d+(?:\.\d+)?)\s*(?:m\s*[²^2]|m2|sqm|square\s+met(?:re|er)s?|squared?\s+met(?:re|er)s?)/
    ];
    for (const pattern of patterns) {
      const match = s.match(pattern);
      if (match && positive(match[1])) return Number(match[1]);
    }
    return 0;
  }

  function dimensionsFromText(text) {
    const match = clean(text).match(/(\d+(?:\.\d+)?)\s*(mm|cm|m)\s*(?:high|tall)?\s*x\s*(?:approximately\s*)?(\d+(?:\.\d+)?)\s*(mm|cm|m)/);
    if (!match) return null;
    return { height: scaleToMetres(match[1], match[2]), width: scaleToMetres(match[3], match[4]) };
  }

  function packCountFromText(text) {
    const s = clean(text);
    const patterns = [/\bpack\s+of\s+(\d+)\b/, /\b(\d+)\s*(?:piece|panel|board|plank|tile)s?\s*(?:per\s+pack|pack)\b/, /\bpack\s*:\s*(\d+)\s*(?:piece|panel|board|plank|tile)s?\b/, /\b(\d+)\s*pack\b/];
    for (const pattern of patterns) {
      const match = s.match(pattern);
      if (match && Number.isInteger(Number(match[1])) && Number(match[1]) > 0) return Number(match[1]);
    }
    return 0;
  }

  function resolveBasis(data) {
    const kind = data.kind;
    const variableSize = String(data.sizeOptions) === 'true';
    let coverage = Number(data.coverage);
    const title = clean(data.title), spec = clean(data.spec), description = clean(data.description);

    if (kind === 'tiles' || kind === 'flooring') {
      if (!positive(coverage) && !variableSize) coverage = areaFromText(description + ' ' + spec + ' ' + title);
      if (positive(coverage) && (!variableSize || positive(data.variantCoverage))) {
        return { mode: 'area', coverage, pieces: 1, unit: 'pack', source: positive(data.coverage) ? 'Product pack coverage' : 'Pack coverage stated in the product information' };
      }
      return null;
    }

    if (kind !== 'panels' && kind !== 'wpc') return null;
    if (variableSize && String(data.variantDimensions) !== 'true') return null;
    if (/hexagon|tessellat|geometric/.test(title)) return null;

    let width = Number(data.widthMm) / 1000;
    let height = Number(data.heightMm) / 1000;
    if ((!positive(width) || !positive(height)) && !variableSize) {
      const dims = dimensionsFromText(spec + ' ' + title + ' ' + description);
      if (dims) { width = dims.width; height = dims.height; }
    }

    let pieces = Number(data.pieces);
    let unit = 'pack';

    if (kind === 'wpc') {
      if (positive(data.effectiveWidthMm) && positive(height)) {
        width = Number(data.effectiveWidthMm) / 1000;
        if (!Number.isInteger(pieces) || pieces < 1) pieces = packCountFromText(title + ' ' + description) || 1;
        return { mode: 'panels', width, height, pieces, unit: pieces === 1 ? 'panel' : 'pack', coverage: width * height * pieces, source: 'Verified installed panel dimensions and purchase unit' };
      }
      coverage = positive(coverage) ? coverage : areaFromText(description + ' ' + spec + ' ' + title);
      if (positive(coverage)) return { mode: 'area', coverage, pieces: 1, unit: 'pack', source: 'Pack coverage stated in the product information' };
      const packDims = clean(description).match(/coverage[^.]{0,80}?(\d+(?:\.\d+)?)\s*(mm|cm|m)\s*(?:high|tall)?\s*x\s*(?:approximately\s*)?(\d+(?:\.\d+)?)\s*(mm|cm|m)[^.]{0,40}?(?:per\s+pack|pack)/);
      if (packDims) {
        const packHeight = scaleToMetres(packDims[1], packDims[2]);
        const packWidth = scaleToMetres(packDims[3], packDims[4]);
        if (positive(packWidth) && positive(packHeight)) return { mode: 'panels', width: packWidth, height: packHeight, pieces: 1, unit: 'pack', coverage: packWidth * packHeight, source: 'Pack coverage dimensions stated in the product information' };
      }
      return null;
    }

    if (!positive(width) || !positive(height)) return null;
    if (!Number.isInteger(pieces) || pieces < 1) {
      pieces = packCountFromText(title + ' ' + description);
      if (!pieces) {
        if (/\bpack\b|\bset of\b/.test(title + ' ' + description)) return null;
        pieces = 1;
        unit = 'panel';
      }
    }
    if (unit !== 'panel') unit = pieces === 1 ? 'panel' : 'pack';
    return { mode: 'panels', width, height, pieces, unit, coverage: width * height * pieces, source: 'Panel dimensions and purchase unit from the product information' };
  }

  function estimate(basis, width, height, waste) {
    [width, height].forEach(n => { if (!positive(n) || n > 1000) throw new Error('Enter positive dimensions no greater than 1,000 metres.'); });
    if (!Number.isFinite(waste) || waste < 0 || waste > 50) throw new Error('Choose an allowance between 0% and 50%.');
    if (!basis || !positive(basis.coverage)) throw new Error('Reliable coverage is not available for this product.');
    const area = width * height;
    let base, quantity;
    if (basis.mode === 'panels') {
      const completePanels = ceil(width / basis.width) * ceil(height / basis.height);
      base = ceil(completePanels / basis.pieces);
      quantity = ceil(ceil(completePanels * (1 + waste / 100)) / basis.pieces);
    } else {
      base = ceil(area / basis.coverage);
      quantity = ceil(area * (1 + waste / 100) / basis.coverage);
    }
    if (!Number.isSafeInteger(quantity) || quantity < 1) throw new Error('Check the measurements before ordering.');
    return { area, base, quantity, supplied: quantity * basis.coverage, waste };
  }

  function obeyQuantityRules(quantity, input) {
    const min = positive(input.min) ? Number(input.min) : 1;
    const step = positive(input.step) ? Number(input.step) : 1;
    const maximum = positive(input.max) ? Number(input.max) : Infinity;
    const result = min + ceil(Math.max(0, quantity - min) / step) * step;
    if (!Number.isInteger(result) || result > maximum) throw new Error('This quantity exceeds the available purchase limit. Contact us for help; the quantity has not been changed.');
    return result;
  }

  function initialiseCalculator(el) {
    if (!el || el.dataset.phfCalculatorReady === 'true') return;
    const basis = resolveBasis(el.dataset);
    if (!basis) return;
    el.dataset.phfCalculatorReady = 'true';
    const details = el.querySelector('details');
    const results = el.querySelector('[data-calc-results]');
    const error = el.querySelector('[data-calc-error]');
    const unavailable = el.querySelector('[data-calc-unavailable]');
    const price = Number(el.dataset.price);
    const variantId = String(el.dataset.variant || '');
    const scope = el.closest('.shopify-section--main-product') || document;
    const formId = 'product-form-main-' + el.dataset.productId + '-' + el.dataset.sectionId;
    if (!details || !results || !error) return;

    details.hidden = false;
    if (unavailable) unavailable.hidden = true;
    const basisText = el.querySelector('[data-calc-basis]');
    if (basisText) basisText.textContent = basis.source + '. ' + basis.coverage.toFixed(3).replace(/0+$/, '').replace(/\.$/, '') + ' m² per ' + basis.unit + '.';
    const disclaimer = el.querySelector('[data-calc-disclaimer]');
    if (disclaimer) disclaimer.textContent = basis.mode === 'panels'
      ? 'Estimate for a rectangular wall with vertically installed panels. Whole panels are counted across and up the wall; offcuts are not reused and openings are not deducted. Walls taller than a panel need joins. The extra allowance is optional and is not a cutting plan. Check measurements, installation and quantities with your installer before ordering.'
      : 'Estimate for a rectangular area, rounded up to complete packs. The extra allowance is optional; complex patterns, cuts and breakages may need more. Check measurements and quantities with your installer before ordering.';

    const wasteSelect = el.querySelector('[data-calc-waste]');
    if (wasteSelect && Array.from(wasteSelect.options).some(o => o.value === el.dataset.allowance)) wasteSelect.value = el.dataset.allowance;
    let calculation = null;
    const invalidate = () => { calculation = null; results.hidden = true; error.hidden = true; };
    const quantityInput = () => {
      const byForm = Array.from(scope.querySelectorAll('input[name="quantity"]')).find(input => input.form && input.form.getAttribute('id') === formId);
      return byForm || scope.querySelector('input[name="quantity"]');
    };
    const calculate = () => {
      invalidate();
      try {
        const unit = Number(el.querySelector('[data-calc-unit]').value);
        const width = Number(el.querySelector('[data-calc-width]').value) / unit;
        const height = Number(el.querySelector('[data-calc-height]').value) / unit;
        const waste = Number(el.querySelector('[data-calc-waste]').value);
        const result = estimate(basis, width, height, waste);
        const quantity = quantityInput();
        if (quantity) result.quantity = obeyQuantityRules(result.quantity, quantity);
        result.supplied = result.quantity * basis.coverage;
        calculation = result;
        const set = (key, value) => { const target = el.querySelector('[data-out-' + key + ']'); if (target) target.textContent = value; };
        set('area', result.area.toFixed(2) + ' m²');
        set('unit', basis.coverage.toFixed(3).replace(/0+$/, '').replace(/\.$/, '') + ' m² / ' + basis.unit);
        set('base', result.base + ' ' + basis.unit + (result.base === 1 ? '' : 's'));
        set('waste', waste + '%');
        set('quantity', result.quantity + ' ' + basis.unit + (result.quantity === 1 ? '' : 's'));
        set('coverage', result.supplied.toFixed(2) + ' m²');
        set('cost', new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: el.dataset.currency || 'GBP' }).format(result.quantity * price / 100));
        const status = el.querySelector('[data-calc-status]');
        if (status) status.textContent = 'Estimate excludes delivery and any checkout adjustments. Quantity is rounded to the permitted order increment.';
        results.hidden = false;
      } catch (e) { error.textContent = e.message; error.hidden = false; }
    };
    const apply = () => {
      if (!calculation) return;
      try {
        const form = document.getElementById(formId);
        const quantity = quantityInput();
        const selected = form && form.querySelector('[name="id"]');
        if (!quantity) throw new Error('The quantity control is not available. Please enter the recommended quantity manually.');
        if (selected && variantId && String(selected.value) !== variantId) throw new Error('The selected variant has changed. Recalculate after the product information updates.');
        quantity.value = obeyQuantityRules(calculation.quantity, quantity);
        quantity.dispatchEvent(new Event('input', { bubbles: true }));
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
        const status = el.querySelector('[data-calc-status]');
        if (status) status.textContent = 'Quantity set to ' + quantity.value + '. Review the selection, then use Add to Cart when ready.';
        quantity.focus({ preventScroll: true });
        quantity.scrollIntoView({ block: 'center', behavior: root.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      } catch (e) { error.textContent = e.message; error.hidden = false; }
    };

    const run = el.querySelector('[data-calc-run]');
    const applyButton = el.querySelector('[data-calc-apply]');
    if (run) run.addEventListener('click', calculate);
    if (applyButton) applyButton.addEventListener('click', apply);
    el.querySelectorAll('input,select').forEach(control => {
      control.addEventListener('input', invalidate);
      control.addEventListener('change', invalidate);
      control.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); calculate(); } });
    });
    const form = document.getElementById(formId);
    if (form) form.addEventListener('variant:change', event => {
      const variant = event.detail && event.detail.variant;
      invalidate();
      if (!variant || String(variant.id) !== variantId) {
        details.hidden = true;
        if (unavailable) {
          unavailable.hidden = false;
          unavailable.textContent = 'Updating product coverage. Please wait for the selected variant to load, or reload this product before calculating.';
        }
      }
    });
  }

  const api = { resolveBasis, estimate, obeyQuantityRules, initialiseCalculator };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root) root.PhfCalculatorApi = api;

  if (root && root.customElements && !root.customElements.get('phf-calculator')) {
    root.customElements.define('phf-calculator', class extends HTMLElement {
      connectedCallback() { requestAnimationFrame(() => initialiseCalculator(this)); }
    });
  }

  function scan() { document.querySelectorAll('phf-calculator').forEach(initialiseCalculator); }
  if (root && root.document) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
    else scan();
    root.addEventListener('load', scan, { once: true });
  }
})(typeof window === 'undefined' ? null : window);
